import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Paper, Snackbar, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useAdminUser } from '../../hooks/useAdminUser';
import { GapSummary, ManifestBadge, RegistryApiError, RegistryTable, lhRegistryApiRef } from './api';
import { ManifestFilter, manifestLabel, manifestTone } from './manifestLabel';
import { RegistryDetail } from './RegistryDetail';
import { STATE_LABEL } from './RegistryColumns';
import { Pill, Seg, TicketLink, fmtAt, shortActor } from './ui';

/** 목록 GAP 칩: 0이면 일치(녹), 있으면 개수(적). 판정 못 한 상태는 그 문구 */
const GapPill = ({ side, s }: { side: string; s?: GapSummary | null }) => {
  if (!s) return <Pill tone="neutral">{side} —</Pill>;
  if (s.count > 0) return <Pill tone="error">{side} {s.count}</Pill>;
  if (s.state === 'ok') return <Pill tone="ok">{side} 일치</Pill>;
  return <Pill tone="neutral" title={s.reason ?? undefined}>{side} {STATE_LABEL[s.state] ?? s.state}</Pill>;
};
const PAGE_SIZES = [25, 50, 100];
const MONO = "'Roboto Mono', monospace";

/** 수집 현황 레지스트리 목록 + 오른쪽 상세. 읽기는 로그인 사용자 전원, 편집은 UI 힌트일 뿐(실제 차단은 서버 403). */
export const RegistryTab = () => {
  const api = useApi(lhRegistryApiRef);
  const { isAdmin } = useAdminUser();
  const [q, setQ] = useState('');
  const [collectYn, setCollectYn] = useState<'' | 'Y' | 'N'>('');
  const [gapOnly, setGapOnly] = useState<'' | 'gap'>('');
  const [manifestFilter, setManifestFilter] = useState<ManifestFilter>('');
  const [badges, setBadges] = useState<Map<number, ManifestBadge>>(new Map());
  const [statuses, setStatuses] = useState<Record<string, { status: string }>>({});
  const [rows, setRows] = useState<RegistryTable[]>();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [error, setError] = useState<{ message: string; disabled: boolean }>();
  const [selected, setSelected] = useState<RegistryTable>();
  const [confirm, setConfirm] = useState<{ next?: RegistryTable }>();
  const [toast, setToast] = useState<string>();
  const dirty = useRef('');
  const onDirty = useCallback((s: string) => {
    dirty.current = s;
  }, []);

  const load = useCallback(() => {
    setError(undefined);
    api
      .listTablesPage({ q: q || undefined, collectYn: collectYn || undefined, gap: gapOnly ? 'any' : undefined, manifest: manifestFilter || undefined, limit: pageSize, offset: page * pageSize })
      .then(r => {
        // 마지막 페이지의 행이 모두 사라졌다면(검색·삭제 후) 첫 페이지로 되돌린다
        if (!r.items.length && page > 0) {
          setPage(0);
          return;
        }
        setRows(r.items);
        setTotal(r.total);
        // 수정 직후 열린 상세의 version 을 최신으로 맞춘다
        setSelected(s => (s ? r.items.find(x => x.id === s.id) ?? s : s));
      })
      .catch(e => setError({ message: e.message, disabled: e instanceof RegistryApiError && e.code === 'REGISTRY_DISABLED' }));
    // manifest PR 은 보조 정보 — GitHub 설정이 없으면(503) 칸만 비운다
    api
      .manifestBadges()
      .then(async b => {
        setBadges(new Map(b.map(x => [Number(x.tableId), x])));
      })
      .catch(() => setBadges(new Map()));
  }, [api, q, collectYn, gapOnly, manifestFilter, page, pageSize]);

  // 근거 티켓 툴팁의 Jira 상태 — 현재 페이지 행의 티켓만 50개씩 나눠 묻는다 (실패하면 서버 클라이언트가 빈 객체를 준다)
  useEffect(() => {
    const keys = [...new Set((rows ?? []).flatMap(r => r.ticketKey ?? []))];
    let live = true;
    Promise.all(Array.from({ length: Math.ceil(keys.length / 50) }, (_, i) => api.ticketStatuses(keys.slice(i * 50, i * 50 + 50)))).then(
      chunks => live && setStatuses(Object.assign({}, ...chunks)),
    );
    return () => { live = false; };
  }, [api, rows]);

  useEffect(() => {
    const t = setTimeout(load, 250); // 검색어 입력 디바운스
    return () => clearTimeout(t);
  }, [load]);

  // 저장하지 않은 변경이 있으면 다른 행을 열거나 닫기 전에 묻는다
  const open = (r?: RegistryTable) => {
    if (r?.id === selected?.id) return;
    if (dirty.current) setConfirm({ next: r });
    else setSelected(r);
  };

  return (
    <Box display="flex" flexWrap="wrap" alignItems="flex-start" style={{ gap: 16 }}>
      <Paper variant="outlined" component="section" aria-label="수집 대상 목록" style={{ flex: '999 1 640px', minWidth: 0 }}>
        <Box p={2} display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 12 }}>
          <TextField
            size="small" variant="outlined" type="search" placeholder="domain · 테이블 · 원천 테이블 검색" inputProps={{ 'aria-label': '검색 (domain · 테이블 · 원천 테이블)' }}
            value={q} onChange={e => { setQ(e.target.value); setPage(0); }} style={{ flex: '1 1 240px' }}
          />
          <Box display="flex" alignItems="center" style={{ gap: 6 }}>
            <Typography variant="caption" color="textSecondary">수집</Typography>
            <Seg value={collectYn} options={[['', '전체'], ['Y', 'Y'], ['N', 'N']]} onChange={v => { setCollectYn(v); setPage(0); }} label="수집 여부" />
          </Box>
          <Seg value={gapOnly} options={[['', 'GAP 전체'], ['gap', 'GAP 있음']]} onChange={v => { setGapOnly(v); setPage(0); }} label="GAP" />
          <Box display="flex" alignItems="center" style={{ gap: 6 }}>
            <Typography variant="caption" color="textSecondary">manifest PR</Typography>
            <Seg
              value={manifestFilter} onChange={v => { setManifestFilter(v); setPage(0); }} label="manifest PR"
              options={[['', 'PR 전체'], ['waiting', 'PR 대기'], ['progress', '진행 중'], ['blocked', '차단'], ['failed', '실패']]}
            />
          </Box>
        </Box>

        {error && <Alert severity={error.disabled ? 'warning' : 'error'}>{error.disabled ? `수집 현황이 비활성 상태입니다: ${error.message}` : error.message}</Alert>}
        {!rows && !error && <Box p={2}><CircularProgress /></Box>}
        {rows && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['domain', 'table_name', '원천', '수집', '컬럼 · GAP', '근거 티켓', '최근 변경'].map(h => (
                    <TableCell key={h} style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => {
                  const b = badges.get(r.id);
                  return (
                    <TableRow key={r.id} hover selected={r.id === selected?.id} style={{ cursor: 'pointer' }} onClick={() => open(r)}>
                      <TableCell>{r.domain}</TableCell>
                      <TableCell>
                        {/* 행 클릭은 마우스용이다 — 키보드는 이 버튼으로 상세를 연다 */}
                        <Link
                          component="button" variant="body2" aria-label={`${r.tableName} 상세 열기`} aria-current={r.id === selected?.id ? 'true' : undefined}
                          onClick={(e: React.MouseEvent) => { e.stopPropagation(); open(r); }} style={{ fontFamily: MONO, textAlign: 'left' }}
                        >
                          {r.tableName}
                        </Link>
                      </TableCell>
                      <TableCell style={{ whiteSpace: 'nowrap' }}>{r.sourceSystem} · {r.sourceType}</TableCell>
                      <TableCell><Pill tone={r.collectYn === 'Y' ? 'solid' : 'neutral'}>{r.collectYn}</Pill></TableCell>
                      <TableCell>
                        <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 4 }}>
                          {r.legacy && <Pill tone="info" title="legacy_import 로 적재된 행 — 보존·대조용이라 수정할 수 없습니다">legacy</Pill>}
                          {r.collectYn === 'N' && <Pill tone="neutral" title="collect_yn=N — GAP 판정 안 함">제외</Pill>}
                          {r.collectYn === 'Y' && r.columnsBaselineVersion === null && <Pill tone="neutral" title="컬럼 정보 없음">컬럼 미등록</Pill>}
                          {r.collectYn === 'Y' && r.columnsBaselineVersion !== null && (
                            <>
                              <Typography variant="body2">{r.columnCount ?? 0}개</Typography>
                              <GapPill side="원천" s={r.gap?.source} />
                              <GapPill side="수집" s={r.gap?.lake} />
                            </>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {r.ticketKey || b ? (
                          <Box display="flex" flexDirection="column" alignItems="flex-start" style={{ gap: 4 }}>
                            {r.ticketKey && (
                              <Tooltip title={`${r.ticketKey} · Jira ${statuses[r.ticketKey]?.status ?? '상태 확인 필요'} — 클릭하면 새 탭으로 열림`}>
                                <span><TicketLink ticketKey={r.ticketKey} /></span>
                              </Tooltip>
                            )}
                            {b && <Pill tone={manifestTone(b)} title={b.botConflict ? '묶음에 속해 있는 동안 BOT PR 이 나타났습니다' : undefined}>{manifestLabel(b)}</Pill>}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell style={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">{fmtAt(r.updatedAt)}</Typography>
                        <Typography variant="caption" color="textSecondary">{shortActor(r.updatedBy)}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {!rows.length && <Box p={3} textAlign="center"><Typography variant="body2" color="textSecondary">조건에 맞는 테이블이 없습니다.</Typography></Box>}
          </TableContainer>
        )}
        {rows && (
          <TablePagination
            component="div" count={total} page={page} rowsPerPage={pageSize} rowsPerPageOptions={PAGE_SIZES}
            onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
            labelRowsPerPage="페이지당" labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}개`}
          />
        )}
      </Paper>

      <Box style={{ flex: '1 1 400px', minWidth: 0, maxWidth: 520 }}>
        {selected ? (
          <RegistryDetail
            key={selected.id} table={selected} readOnly={!isAdmin || selected.legacy} onClose={() => open(undefined)} onReload={load} onDirty={onDirty}
            onSaved={m => { setToast(m); load(); }}
          />
        ) : (
          <Box border={1} borderColor="divider" borderRadius={8} px={3} py={5} textAlign="center" style={{ borderStyle: 'dashed' }}>
            <Typography variant="body2" color="textSecondary">
              테이블 행을 누르면 상세·편집과 위키식 변경 이력이 여기에 열립니다.<br />근거 티켓에 마우스를 올리면 Jira 상태를 미리 볼 수 있습니다.
            </Typography>
          </Box>
        )}
      </Box>

      <Dialog open={!!confirm} onClose={() => setConfirm(undefined)} aria-labelledby="cfm-title">
        <DialogTitle id="cfm-title">저장하지 않은 변경이 있습니다</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{confirm?.next ? '다른 테이블을 열면' : '상세를 닫으면'} 작성 중인 변경이 사라집니다.</Typography>
          <Typography variant="caption" color="textSecondary">작성 중: {dirty.current}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setConfirm(undefined)}>계속 편집</Button>
          <Button
            variant="contained" color="primary"
            onClick={() => {
              dirty.current = '';
              setSelected(confirm?.next);
              setConfirm(undefined);
            }}
          >
            변경 버리고 이동
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(undefined)} message={toast} />
    </Box>
  );
};
